import * as api from '@gitbook/api-client';
import { webConfig } from '@gitbook/env';
import { DEFAULT_CONTENT_TYPE, getOrganizationFilePublicURL } from '@gitbook/util-files';

import { apiClient } from '../../state';

/**
 * A file uploaded to an organization.
 */
export type UploadedOrganizationFile = {
    /** Key of the object in the storage bucket. */
    key: string;
    /** Public URL the file is served at. */
    url: string;
    /** Name the file was uploaded under. */
    filename: string;
    /** Content type it was uploaded with, and is served as. */
    contentType: string;
};

/**
 * Upload a file to an organization, and return where it can be read from.
 *
 * The bytes go straight to the storage bucket through a presigned URL, so they never pass through
 * the API. Nothing here is tied to a feature: an organization file is simply a file belonging to an
 * organization, and the key it comes back with is minted server-side.
 */
export async function uploadOrganizationFile(input: {
    organizationId: string;
    file: File;
    /** Type to store the file as, when the caller knows better than the browser does. */
    contentType?: string;
}): Promise<UploadedOrganizationFile> {
    const { organizationId, file } = input;
    const contentType = input.contentType || file.type || DEFAULT_CONTENT_TYPE;

    const { data } = await apiClient.storage.generateStorageUploadUrl(organizationId, {
        file: { name: file.name, size: file.size, type: contentType },
        kind: api.StorageUploadKind.OrganizationFile,
    });

    const response = await fetch(data.url, {
        method: 'PUT',
        body: file,
        // Has to be exactly the type the URL was signed for, or the bucket rejects the signature.
        headers: { 'Content-Type': contentType },
    });

    if (!response.ok) {
        throw new Error(`Failed to upload ${file.name}: ${response.statusText}`);
    }

    return {
        key: data.key,
        url: getOrganizationFilePublicURL(webConfig.services.content.host, data.key),
        filename: file.name,
        contentType,
    };
}

/**
 * Upload several files to an organization at once, in the order they were given.
 */
export function uploadOrganizationFiles(input: {
    organizationId: string;
    files: Array<{ file: File; contentType?: string }>;
}): Promise<UploadedOrganizationFile[]> {
    const { organizationId, files } = input;

    return Promise.all(
        files.map(({ file, contentType }) =>
            uploadOrganizationFile({
                organizationId,
                file,
                ...(contentType ? { contentType } : {}),
            })
        )
    );
}
